import { isNonNullable, nonNullable, unique } from 'lib/array/arrayUtils'
import getFlightDetailsByOrderItems from '../../client/lib/flights/getFlightDetailsByOrderItems'
import { getInsuranceCountryTwoLetterCodeByName, getInsuranceCountryNameByTwoLetterCode } from '@luxuryescapes/lib-regions'
import getOrderAccommodationStartDate from 'lib/order/getOrderAccommodationStartDate'
import getOrderAccommodationEndDate from 'lib/order/getOrderAccommodationEndDate'
import { isEsimExcludedCountry } from './esimGeoExclusion'
import { SEVEN_DAYS_MS } from 'loyalty/lib/loyaltyEsimUtils'
import moment from 'moment'

function bedbankDateToString(val: unknown): string | undefined {
  if (val == null) return undefined
  if (typeof val === 'string') return val
  if (moment.isMoment(val)) return val.format('YYYY-MM-DD')
  return undefined
}

type FlightDetailsByItem = Record<string, App.FlightOrderItemDetails | undefined>

/** Uses the first (outbound) leg's arrival so eSIM destination is where you're travelling to, not the return arrival (e.g. Melbourne for MEL↔DPS). */
function getFlightDestinationFromDetails(byItem: FlightDetailsByItem, order: App.Order): string | undefined {
  const firstItem = order.flightItems.find(item => item.status !== 'cancelled')
  if (!firstItem) return undefined
  const details = byItem[firstItem.id]
  const legs = details?.bookingJourney?.legs
  if (!legs?.length) return undefined
  const outboundLeg = legs[0]
  const flights = outboundLeg?.flights
  if (!flights?.length) return undefined
  const outboundLastFlight = flights[flights.length - 1] as { arrivalCity?: string; arrivalCountry?: string; arrivalAirportName?: string }
  const { arrivalCity, arrivalCountry, arrivalAirportName } = outboundLastFlight
  if (arrivalCity && arrivalCountry) return `${arrivalCity}, ${arrivalCountry}`
  if (arrivalAirportName && arrivalCountry) return `${arrivalAirportName}, ${arrivalCountry}`
  if (arrivalCountry) return arrivalCountry
  if (arrivalCity) return arrivalCity
  if (arrivalAirportName) return arrivalAirportName
  return undefined
}

function getFlightDateRangeFromDetails(byItem: FlightDetailsByItem, order: App.Order): { startDate: string | undefined; endDate: string | undefined } {
  const firstItem = order.flightItems.find(item => item.status !== 'cancelled')
  if (!firstItem) return { startDate: undefined, endDate: undefined }
  const details = byItem[firstItem.id]
  const legs = details?.bookingJourney?.legs
  if (!legs?.length) return { startDate: undefined, endDate: undefined }
  const firstLeg = legs[0]
  const lastLeg = legs[legs.length - 1]
  const firstFlight = firstLeg?.flights?.[0] as { departingDate?: string } | undefined
  const lastFlights = lastLeg?.flights
  const lastFlight = lastFlights?.length ? (lastFlights[lastFlights.length - 1] as { arrivalDate?: string; departingDate?: string }) : undefined
  const startDate = firstFlight?.departingDate
  const endDate = lastFlight?.arrivalDate ?? lastFlight?.departingDate ?? startDate
  return { startDate, endDate }
}

export interface EligibleEsimOrder {
  orderId: string
  customerId: string
  destination: string
  dates: string
  departureDate: Date
  isWithinSevenDays: boolean
  tripEnded: boolean
  availableDate: string | undefined
  tier: App.LoyaltyTier | undefined
}

function formatDate(date: Date, locale: string): string {
  return date.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' })
}

function formatDateRange(startDate: string | undefined, endDate: string | undefined, locale: string): string {
  if (!startDate) return ''
  const start = new Date(startDate)
  const end = endDate ? new Date(endDate) : undefined
  if (!end) return formatDate(start, locale)
  return `${start.toLocaleDateString(locale, { day: 'numeric', month: 'short' })} – ${formatDate(end, locale)}`
}

function formatLocalizedList(values: Array<string>, locale: string): string {
  if (values.length <= 1) return values[0] ?? ''

  if (typeof Intl.ListFormat === 'function') {
    return new Intl.ListFormat(locale, {
      style: 'long',
      type: 'conjunction',
    }).format(values)
  }

  return values.slice(0, -1).join(', ') + ' & ' + values[values.length - 1]
}

export type OrderFlightDetailsState = App.ApiStateRecord<{ flight?: App.FlightOrderItemDetails }>

export function getOrderInternationalCountryCodes(
  order: App.Order,
  accountRegion: string,
  flightDetails?: OrderFlightDetailsState,
  cruiseCountryCodesForOrder?: Array<string>,
  esimExcludedCountryCodes?: Array<string>,
): Array<string> {
  const accommodationCodes = order.items.map(item => item.offer.property?.geoData?.countryCode)
  const bedbankCodes = order.bedbankItems.map(item => item.offer?.property?.address?.countryCode)
  // Cruises: itinerary/port country codes are not on the order payload; pass cruiseCountryCodesForOrder when available (e.g. from useEsimPromoCodes snapshot fetch).
  // Tour API returns countries_visited as names (e.g. "Egypt") or sometimes codes; map to 2-letter codes for eligibility
  const tourCodes = order.tourItems
    .filter(item => item.status !== 'cancelled')
    .flatMap(item => (item.tour?.countriesVisited ?? []).map((c): string | undefined => {
      if (typeof c !== 'string' || !c.trim()) return undefined
      if (c.length === 2) return c.trim().toUpperCase()
      return getInsuranceCountryTwoLetterCodeByName(c)
    }))
    .filter(isNonNullable)
  let allCodes = nonNullable(unique([...accommodationCodes, ...bedbankCodes, ...tourCodes]))

  if (cruiseCountryCodesForOrder?.length) {
    allCodes = nonNullable(unique([...allCodes, ...cruiseCountryCodesForOrder]))
  }

  // Flights: use destination country codes from the order payload (set at map time, like hotels/tours)
  // so we don't depend on state.orders.flightDetails. Fall back to flightDetails when order has no codes (e.g. legacy).
  if (order.flightItems.length) {
    const fromOrder = order.flightItems.flatMap((item: App.OrderFlightItem) => item.destinationCountryCodes ?? [])
    if (fromOrder.length) {
      allCodes = nonNullable(unique([...allCodes, ...fromOrder]))
    } else if (flightDetails) {
      const byItem = getFlightDetailsByOrderItems(order, flightDetails)
      if (byItem) {
        const flightCodes = order.flightItems.flatMap((item: App.OrderFlightItem) => {
          const details = byItem[item.id]
          if (!details?.bookingJourney?.legs) return []
          return details.bookingJourney.legs.map((leg: { arrivalCountryCode?: string }) => leg.arrivalCountryCode).filter(isNonNullable)
        })
        allCodes = nonNullable(unique([...allCodes, ...flightCodes]))
      }
    }
  }

  const region = accountRegion.toUpperCase()
  return allCodes.filter(cc => cc.toUpperCase() !== region && !isEsimExcludedCountry(cc, esimExcludedCountryCodes ?? []))
}

export function getOrderDepartureDate(order: App.Order): Date | undefined {
  const accommodationStart = getOrderAccommodationStartDate(order)
  if (accommodationStart) return new Date(accommodationStart)

  const bedbankItem = order.bedbankItems.find(item => item.status !== 'cancelled')
  const bedbankCheckIn = bedbankDateToString(bedbankItem?.checkIn)
  if (bedbankCheckIn) return new Date(bedbankCheckIn)

  const cruiseItem = order.cruiseItems.find(item => item.status !== 'cancelled')
  if (cruiseItem?.departureDate) return new Date(cruiseItem.departureDate)

  const tourItem = order.tourItems.find(item => item.status !== 'cancelled')
  if (tourItem?.tour?.startDate) return new Date(tourItem.tour.startDate)

  const flightItem = order.flightItems.find(item => item.status !== 'cancelled')
  if (flightItem?.departureDate) return new Date(flightItem.departureDate)

  return undefined
}

export function getOrderEndDate(order: App.Order): Date | undefined {
  const accommodationEnd = getOrderAccommodationEndDate(order)
  if (accommodationEnd) return new Date(accommodationEnd)

  const bedbankItem = order.bedbankItems.find(item => item.status !== 'cancelled')
  const bedbankCheckOut = bedbankDateToString(bedbankItem?.checkOut)
  if (bedbankCheckOut) return new Date(bedbankCheckOut)

  const cruiseItem = order.cruiseItems.find(item => item.status !== 'cancelled')
  if (cruiseItem?.arrivalDate) return new Date(cruiseItem.arrivalDate)

  const tourItem = order.tourItems.find(item => item.status !== 'cancelled')
  if (tourItem?.tour?.endDate) return new Date(tourItem.tour.endDate)

  const flightItem = order.flightItems.find(item => item.status !== 'cancelled')
  if (flightItem?.arrivalDate) return new Date(flightItem.arrivalDate)

  return undefined
}

// Countries that appear in bookings but aren't in the insurance countries list.
// Mirrors the aliases maintained in svc-lux-loyalty extractEsimOrderData.util.ts.
const countryCodeFallbackNames: Record<string, string> = {
  CN: 'China',
  TW: 'Taiwan',
  BN: 'Brunei',
  KR: 'South Korea',
  VA: 'Vatican City',
}

function getCountryNameByCode(cc: string): string | undefined {
  return getInsuranceCountryNameByTwoLetterCode(cc) ?? countryCodeFallbackNames[cc.toUpperCase()]
}

function formatCountryCodesAsDestination(
  countryCodes: Array<string>,
  locale: string,
  internationalTripLabel: string,
): string {
  const names = countryCodes
    .map(cc => getCountryNameByCode(cc))
    .filter(isNonNullable)
  if (!names.length) return internationalTripLabel
  if (names.length === 1) return names[0]
  return formatLocalizedList(names, locale)
}

function getOrderDestinationDisplay(
  order: App.Order,
  locale: string,
  internationalTripLabel: string,
  internationalCountryCodes?: Array<string>,
): string {
  const firstAccommodation = order.items.find(item => item.offer?.property?.geoData?.countryCode || item.offer?.property?.geoData?.country)
  if (firstAccommodation?.offer?.property?.geoData) {
    const geo = firstAccommodation.offer.property.geoData
    const name = (geo.countryCode ? getCountryNameByCode(geo.countryCode) : undefined) ?? geo.country
    if (name) return name
  }

  const firstBedbank = order.bedbankItems[0]
  if (firstBedbank?.offer?.property?.address) {
    const addr = firstBedbank.offer.property.address
    const name = (addr.countryCode ? getCountryNameByCode(addr.countryCode) : undefined) ?? addr.countryName
    if (name) return name
  }

  if (internationalCountryCodes?.length) {
    return formatCountryCodesAsDestination(internationalCountryCodes, locale, internationalTripLabel)
  }
  return internationalTripLabel
}

export type CruiseCountryCodesByOrderId = Record<string, Array<string>>

export function getEligibleEsimOrders(
  orders: Array<App.Order>,
  accountRegion: string,
  locale: string,
  internationalTripLabel: string,
  flightDetails?: OrderFlightDetailsState,
  cruiseCountryCodesByOrderId?: CruiseCountryCodesByOrderId,
  esimExcludedCountryCodes?: Array<string>,
): Array<EligibleEsimOrder> {
  return orders
    .filter(order => order.status !== 'cancelled')
    .map(order => {
      const internationalCodes = getOrderInternationalCountryCodes(
        order,
        accountRegion,
        flightDetails,
        cruiseCountryCodesByOrderId?.[order.id],
        esimExcludedCountryCodes,
      )
      if (!internationalCodes.length) return null

      const departureDate = getOrderDepartureDate(order)
      if (!departureDate) return null

      const endDate = getOrderEndDate(order)

      const now = new Date()
      const tripEnded = endDate ? endDate < now : false
      const sevenDaysBefore = new Date(departureDate.getTime() - SEVEN_DAYS_MS)
      const isWithinSevenDays = now >= sevenDaysBefore && (endDate ? endDate >= now : true)

      const byItem = flightDetails && order.flightItems.length
        ? getFlightDetailsByOrderItems(order, flightDetails)
        : undefined

      let destination: string
      let dates: string
      if (byItem && Object.keys(byItem).length > 0) {
        const flightDestination = getFlightDestinationFromDetails(byItem, order)
        const flightDateRange = getFlightDateRangeFromDetails(byItem, order)
        destination = flightDestination ?? getOrderDestinationDisplay(order, locale, internationalTripLabel, internationalCodes)
        dates = flightDateRange.startDate
          ? formatDateRange(flightDateRange.startDate, flightDateRange.endDate, locale)
          : formatDateRange(
              getOrderAccommodationStartDate(order) ?? bedbankDateToString(order.bedbankItems[0]?.checkIn),
              getOrderAccommodationEndDate(order) ?? bedbankDateToString(order.bedbankItems[0]?.checkOut),
              locale,
            )
      } else {
        const startDate = getOrderAccommodationStartDate(order) ?? bedbankDateToString(order.bedbankItems[0]?.checkIn)
        const endDate = getOrderAccommodationEndDate(order) ?? bedbankDateToString(order.bedbankItems[0]?.checkOut)
        destination = getOrderDestinationDisplay(order, locale, internationalTripLabel, internationalCodes)
        // For flight-only orders we often have no accommodation dates; use departure date for display
        dates = startDate ? formatDateRange(startDate, endDate, locale) : (departureDate ? formatDate(departureDate, locale) : '')
      }

      return {
        orderId: order.id,
        customerId: order.customerId,
        destination,
        dates,
        departureDate,
        isWithinSevenDays,
        tripEnded,
        availableDate: !isWithinSevenDays && !tripEnded ? formatDate(sevenDaysBefore, locale) : undefined,
        tier: order.loyaltyDetails?.tier,
      }
    })
    .filter(isNonNullable)
    .sort((a, b) => a.departureDate.getTime() - b.departureDate.getTime())
}
